import React, { useState, useEffect, useMemo } from 'react';
import { IAsideLeftProps } from './aside-left.type';
import { Link, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  Settings,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from 'lucide-react';
import { navbar, NavbarItem } from '../../constants/menu/menu';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { buildProfileSlug } from '../../utils/profileSlug';
import { refreshBrandThreadUnread, useBrandThreadUnread } from '../../hooks/use-brand-thread-unread';

const STORAGE_KEY = 'cab-sidebar-expanded';

// Menu id → feature flag key; ẩn mục khi admin tắt tính năng.
const ITEM_FLAG: Record<string, string> = {
  reels: 'feature.reels',
  studio: 'feature.studio',
  'dao-ca': 'feature.gamification',
  donate: 'feature.donate',
};

// Mục phụ (bên dưới đường kẻ) — trước đây hard-code, nay gom vào 1 mảng.
const secondaryItems: NavbarItem[] = [
  { id: 'post-uncheck', name: 'Bài viết chờ duyệt', link: '/post-uncheck', Icon: ClipboardList },
  { id: 'settings', name: 'Cài đặt', link: '/profile?tab=settings', Icon: Settings },
];

// Active khi trùng path; với link có ?tab= thì phải trùng cả tab (Bạn bè vs Cài đặt cùng /profile).
// myProfilePath: trang cá nhân của mình bị đổi URL /profile → /user/{handle}, nên coi 2 đường là một
// (nếu không, các mục trỏ /profile như "Bạn bè"/"Cài đặt" sẽ không bao giờ sáng khi đang ở /user/{handle}).
const useIsActive = (myProfilePath: string) => {
  const location = useLocation();
  return (link: string) => {
    // eslint-disable-next-line prefer-const
    let [path, query] = link.split('?');
    if (path === '/profile' && myProfilePath) path = myProfilePath;
    if (path === '/') return location.pathname === '/';
    const samePath =
      location.pathname === path || location.pathname.startsWith(`${path}/`);
    if (!samePath) return false;
    if (query) {
      const linkTab = new URLSearchParams(query).get('tab');
      if (linkTab) {
        const curTab = new URLSearchParams(location.search).get('tab');
        return curTab === linkTab;
      }
    }
    return true;
  };
};

export const AsideLeft: React.FC<IAsideLeftProps> = ({ isMobile }) => {
  const profile = useSelector((s: RootState) => s.features.auth.profile);
  // Dựng URL canonical trang cá nhân của mình y hệt profile.page.tsx (username sạch hoặc {tên}-{id}).
  const myProfilePath = useMemo(() => {
    if (!profile) return '';
    const uname = (profile.username || '').toLowerCase();
    const clean = /^[a-z0-9_]{3,30}$/.test(uname);
    const canonical = clean
      ? uname
      : profile.sequenceId
      ? buildProfileSlug(profile.fullname, profile.sequenceId)
      : '';
    return canonical ? `/user/${canonical}` : '';
  }, [profile?.username, profile?.sequenceId, profile?.fullname]);
  const isActive = useIsActive(myProfilePath);
  // Chấm đỏ khi phòng trao đổi có tin chưa đọc: creator ở "Hợp tác quảng cáo", người đặt ở "Quảng cáo".
  const loggedIn = !!profile;
  const unread = useBrandThreadUnread(loggedIn);
  const location = useLocation();
  useEffect(() => {
    if (loggedIn) refreshBrandThreadUnread();
  }, [location.pathname, loggedIn]);
  const hasUnreadDot = (item: NavbarItem) =>
    (item.id === 'brand-deals' && unread.asCreator > 0) || (item.id === 'ads' && unread.asOrderer > 0);
  const { isOn } = useFeatureFlags();
  // Gán nhóm cho từng mục (forward-fill từ mục có `group`) rồi ẩn mục bị admin tắt.
  // Nhờ vậy nếu mục ĐẦU nhóm bị ẩn theo feature-flag, header nhóm vẫn gắn đúng mục kế tiếp.
  const visibleNavbar = (() => {
    let cur = '';
    return navbar
      .map((it) => {
        if (it.group) cur = it.group;
        return { ...it, _group: cur } as NavbarItem & { _group: string };
      })
      .filter((it) => {
        const flag = ITEM_FLAG[String(it.id)];
        return !flag || isOn(flag);
      });
  })();

  const [expanded, setExpanded] = useState<boolean>(() => {
    if (isMobile) return true;
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Đồng bộ độ rộng vùng nội dung: <main> dùng var --w-side / --w-side-sm.
  // Đổi 2 biến này khi thu/mở là main tự né, không cần sửa layout.
  useEffect(() => {
    if (isMobile) return;
    try {
      localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      /* ignore */
    }
    const w = expanded ? '228px' : '88px';
    document.documentElement.style.setProperty('--w-side', w);
    document.documentElement.style.setProperty('--w-side-sm', w);
  }, [expanded, isMobile]);

  const showLabels = isMobile || expanded;

  const renderItem = (item: NavbarItem) => {
    const Icon = item.Icon as LucideIcon;
    const active = isActive(item.link);
    const gold = item.accent === 'gold';

    return (
      <li key={item.id}>
        <Link
          to={item.link}
          title={item.name}
          className={[
            'group relative flex items-center rounded-xl transition-colors duration-200',
            showLabels ? 'gap-2.5 px-2.5 h-11' : 'justify-center h-11 w-11 mx-auto',
            active
              ? 'bg-[#0057FF] text-white shadow-[0_4px_12px_rgba(0,87,255,0.35)]'
              : gold
              ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-slate-700'
              : 'text-[#98A2B3] hover:bg-[#EAEFFA] hover:text-[#475467] dark:text-slate-300 dark:hover:bg-slate-700',
          ].join(' ')}
        >
          {Icon && (
            <Icon
              size={20}
              className="shrink-0"
              {...(gold && !active ? { fill: 'currentColor' } : {})}
            />
          )}
          {hasUnreadDot(item) && (
            <span
              aria-label="Có tin nhắn mới"
              className={`absolute size-2 rounded-full bg-rose-500 ${showLabels ? 'left-7 top-2.5' : 'right-2 top-2'}`}
            />
          )}
          {showLabels && (
            <span className="whitespace-nowrap text-[14px] font-normal tracking-[-0.01em]">
              {item.name}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Nút thu/mở dạng tròn nổi trên viền phải của thanh (chỉ desktop) */}
      {!isMobile && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? 'Thu gọn' : 'Mở rộng'}
          aria-label={expanded ? 'Thu gọn menu' : 'Mở rộng menu'}
          style={{ left: `${(expanded ? 228 : 88) - 14}px` }}
          className="fixed top-[75px] z-[100] hidden h-7 w-7 items-center justify-center rounded-full border border-[#DEE0E6] bg-white text-[#18191E] shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-[left] duration-300 hover:bg-slate-50 xl:flex dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
        >
          {expanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
        </button>
      )}

      <aside
        className={
          isMobile
            ? 'w-full h-[calc(100vh-64px)] overflow-y-auto'
            : `fixed top-0 left-0 z-[99] h-screen pt-[--m-top] overflow-y-auto overflow-x-hidden pb-0 transition-[width] duration-300 max-xl:w-full max-xl:-translate-x-full ${
                expanded ? 'w-[228px]' : 'w-[88px]'
              }`
        }
      >
        <div
          className={`border-r border-r-[#DEE0E6] dark:border-r-slate-700 ${
            showLabels ? 'px-3' : 'px-2'
          } ${
            isMobile
              ? 'h-full py-3'
              : 'bg-white shadow-sm min-h-[calc(100vh-4.75rem)] relative z-30 pt-8 pb-10 dark:!bg-slate-700'
          }`}
        >
          <nav id="side">
            <ul className="flex flex-col gap-2">
              {visibleNavbar.map((item, i) => {
                const newGroup = item._group && item._group !== visibleNavbar[i - 1]?._group;
                return (
                  <React.Fragment key={item.id}>
                    {newGroup && showLabels && (
                      <li
                        className={`${i !== 0 ? 'mt-3 border-t border-[#EAEFFA] pt-3 dark:border-slate-600' : ''} mb-1 px-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#344054] dark:text-slate-100`}
                      >
                        {item._group}
                      </li>
                    )}
                    {newGroup && !showLabels && i !== 0 && (
                      <li aria-hidden className="mx-auto my-1 h-px w-6 rounded bg-[#EAEFFA] dark:bg-slate-600" />
                    )}
                    {renderItem(item)}
                  </React.Fragment>
                );
              })}
            </ul>
          </nav>
          <nav
            id="side"
            className="border-t-[2px] border-[#EAEFFA] pt-6 mt-3 dark:border-slate-800"
          >
            <ul className="flex flex-col gap-2">{secondaryItems.map(renderItem)}</ul>
          </nav>
        </div>
      </aside>
    </>
  );
};
